/**
 * Screen Reader Announcer
 *
 * Provides live region announcements for screen readers.
 * Used to announce drawer open/close events and other dynamic content changes.
 *
 * @see docs/stories/DRAWER-5.2-implement-accessibility-audit.md
 */

let announcer: HTMLDivElement | null = null;

/**
 * Get or create the announcer element
 */
function getAnnouncer(): HTMLDivElement {
  if (!announcer) {
    announcer = document.createElement("div");
    announcer.setAttribute("aria-live", "polite");
    announcer.setAttribute("aria-atomic", "true");
    announcer.setAttribute("role", "status");
    announcer.className = "sr-only";
    announcer.style.cssText = `
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    `;
    document.body.appendChild(announcer);
  }
  return announcer;
}

/**
 * Announce a message to screen readers
 *
 * @param message - The message to announce
 * @param priority - 'polite' waits for user idle, 'assertive' interrupts
 *
 * @example
 * ```ts
 * announce("Task drawer opened");
 * announce("Error: Unable to save", "assertive");
 * ```
 */
export function announce(
  message: string,
  priority: "polite" | "assertive" = "polite"
): void {
  const element = getAnnouncer();
  element.setAttribute("aria-live", priority);

  // Clear and set message with a delay for screen reader to pick up change
  element.textContent = "";
  requestAnimationFrame(() => {
    element.textContent = message;
  });
}

/**
 * Announce drawer opened
 */
export function announceDrawerOpened(title: string, layer: number): void {
  const layerText = layer > 1 ? `, layer ${layer}` : "";
  announce(`${title} drawer opened${layerText}`);
}

/**
 * Announce drawer closed
 */
export function announceDrawerClosed(title: string): void {
  announce(`${title} drawer closed`);
}

/**
 * Announce max drawers reached
 */
export function announceMaxDrawersReached(): void {
  announce("Maximum 3 drawers open. Close a drawer to open another.", "assertive");
}

export default announce;
